import FlowInitializer, { FlowParamsContext } from '@/app/FlowInitializer';
import MainFlowPanel from '@/app/MainFlowPanel';
import RightPanel from '@/app/RightPanel';
import { BackButton } from '@/components/BackButton';
import { selectedDraftFlowState } from '@/state';
import type { AppFlowParams } from '@rock/types';
import { ReactFlowProvider } from '@xyflow/react';
import { useAtomValue } from 'jotai';
import { type FC, type PropsWithChildren, useContext } from 'react';

/**
 * The ReactFlow guide editor shared by the default-equipment guide, space guides, and
 * equipment-type guides — the canvas shows once a draft flow is selected in the right panel.
 *
 * It takes the whole content pane, so a guide opened from a record's Guides table gets `showBack` —
 * a floating arrow over the canvas's empty top-left corner (the zoom slider and minimap sit along
 * the bottom) is the only way back out. The default guide, reached from the nav, doesn't need one.
 */
export const GuideEditorPage: FC<{
    params: AppFlowParams;
    flowId?: string;
    /** Where this editor's guides live in the URL, so switching guides is a navigation. */
    editBase: string;
    showBack?: boolean;
}> = ({ params, flowId, editBase, showBack }) => (
    <ReactFlowProvider>
        <FlowInitializer params={params} flowId={flowId} editBase={editBase}>
            <div className="relative w-full h-full grid grid-cols-4 grid-rows-1">
                {showBack && (
                    <div className="absolute left-2 top-2 z-10 rounded-md bg-background/90 shadow-sm">
                        <BackButton label="Back" />
                    </div>
                )}
                <div className="col-span-3">
                    <IfFlowSelected>
                        <MainFlowPanel />
                    </IfFlowSelected>
                </div>
                <div className="col-auto bg-slate-100 border-l-2 flex">
                    <RightPanel />
                </div>
            </div>
        </FlowInitializer>
    </ReactFlowProvider>
);

const IfFlowSelected: FC<PropsWithChildren> = ({ children }) => {
    const params = useContext(FlowParamsContext);
    const selectedDraftFlowLoadable = useAtomValue(selectedDraftFlowState(params));
    if (selectedDraftFlowLoadable.state === 'hasData' && selectedDraftFlowLoadable.data) {
        return children;
    }
    return null;
};
